import React, { useState, useEffect } from 'react';
import { useTheme } from '../../../../context/ThemeContext';
import { useAuth } from '../../../../context/AuthContext';
import { apiGet } from '../../../../utils/api';
import { Users, UserCheck, Mail, Zap, Shield } from 'lucide-react';

const ClientOverviewPanel = ({ clientId }) => {
    const { theme } = useTheme();
    const { currentUser } = useAuth();
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);

    const cq = clientId ? `?client_id=${clientId}` : '';
    useEffect(() => { fetchStats(); }, [clientId]);

    const fetchStats = async () => {
        setLoading(true);
        try {
            const res = await apiGet(`/client_manager/dashboard${cq}`);
            if (res.ok) setStats(await res.json());
        } catch {} finally { setLoading(false); }
    };

    const dark = theme === 'dark';

    if (loading) return <div className="text-center py-8 text-gray-500 font-rasa">Loading...</div>;
    if (!stats) return <div className="text-center py-8 text-gray-500 font-rasa">Failed to load dashboard</div>;

    const cards = [
        { label: 'Total Users', value: stats.total_users, icon: Users, color: 'blue' },
        { label: 'Active Users', value: stats.active_users, icon: UserCheck, color: 'green' },
        { label: 'Pending Invitations', value: stats.pending_invitations, icon: Mail, color: 'orange' },
        { label: 'Features Enabled', value: stats.features_enabled, icon: Zap, color: 'purple' },
        { label: 'Roles', value: stats.roles_count || 0, icon: Shield, color: 'indigo' },
    ];

    const colorMap = {
        blue: dark ? 'bg-blue-900/30 text-blue-400' : 'bg-blue-50 text-blue-600',
        green: dark ? 'bg-green-900/30 text-green-400' : 'bg-green-50 text-green-600',
        orange: dark ? 'bg-orange-900/30 text-orange-400' : 'bg-orange-50 text-orange-600',
        purple: dark ? 'bg-purple-900/30 text-purple-400' : 'bg-purple-50 text-purple-600',
        indigo: dark ? 'bg-indigo-900/30 text-indigo-400' : 'bg-indigo-50 text-indigo-600',
    };

    return (
        <div className="space-y-6">
            <div>
                <h2 className="text-lg font-dm-serif">{stats.company_name}</h2>
                <p className="text-sm text-gray-500 font-rasa mt-1">Client organization overview</p>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {cards.map(({ label, value, icon: Icon, color }) => (
                    <div key={label} className={`p-4 rounded-lg border ${dark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
                        <div className="flex items-center gap-3">
                            <div className={`p-2 rounded-lg ${colorMap[color]}`}>
                                <Icon size={20} />
                            </div>
                            <div>
                                <p className="text-2xl font-dm-serif">{value}</p>
                                <p className="text-xs text-gray-500 font-rasa">{label}</p>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default ClientOverviewPanel;
